import {
  API_PREFIX,
  APP_SESSION_HEADER,
  COMPANY_HEADER,
  IDEMPOTENCY_HEADER,
  apiErrorSchema,
  looksTechnical,
} from "@cafegestor/shared";
import { getActiveCompanyId } from "./company-store";

/**
 * Configuração do cliente, feita UMA vez pelo app na inicialização (A0): o ERP Web informa a
 * URL da API (vazia em desenvolvimento: proxy do Vite) e o token do login (Supabase no
 * navegador); o CaféGestor Vendedor informa os seus. Nada de segredo aqui: só a URL pública
 * e uma função que devolve o token de acesso do usuário logado.
 */
export type ClientConfig = {
  /** URL base da API (sem o prefixo /api) */
  baseUrl: string;
  /** token de acesso atual (renovado pelo app); null sem sessão */
  getAccessToken: () => Promise<string | null>;
  /**
   * A1 — CaféGestor Vendedor: segredo da sessão de aplicativo DESTA empresa (vai no cabeçalho
   * `x-app-session` das chamadas com empresa). O ERP Web não usa (null).
   */
  getAppSession: (companyId: string) => Promise<string | null>;
  /** A1: a API recusou a sessão de aplicativo (encerrada, expirada, sem acesso ao app) */
  onAppSessionRejected: (err: ApiRequestError) => void;
};

const config: ClientConfig = {
  baseUrl: "",
  getAccessToken: async () => null,
  getAppSession: async () => null,
  onAppSessionRejected: () => undefined,
};

/** Erro da API que invalida a sessão de aplicativo (o app precisa abrir outra ou sair). */
export function isAppSessionError(err: unknown): boolean {
  return (
    err instanceof ApiRequestError &&
    (err.code.startsWith("APP_SESSAO_") || err.code === "SEM_ACESSO_APLICATIVO")
  );
}

export function configureClient(next: Partial<ClientConfig>): void {
  Object.assign(config, next);
}

/** URL base da API configurada pelo app. */
export const apiBaseUrl = (): string => config.baseUrl;
const currentAccessToken = (): Promise<string | null> => config.getAccessToken();

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    /** detalhes do erro (ex.: problemas encontrados no XML) */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export type ApiOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Chave de idempotência (operações críticas). Gere UMA por intenção do usuário. */
  idempotencyKey?: string;
  /** Envia a empresa ativa (padrão: true). */
  withCompany?: boolean;
  /** Para testes: token explícito. */
  token?: string | null;
};

/**
 * Chamada à API do CaféGestor. O frontend apenas SOLICITA:
 * validação, permissão e regras de negócio ficam na API.
 */
export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, idempotencyKey, withCompany = true, token, headers, ...init } = opts;
  const accessToken = token !== undefined ? token : await currentAccessToken();
  const companyId = withCompany ? getActiveCompanyId() : null;
  const appSession = companyId ? await config.getAppSession(companyId) : null;

  const res = await fetch(`${config.baseUrl}${API_PREFIX}${path}`, {
    ...init,
    credentials: "omit",
    headers: {
      accept: "application/json",
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...(companyId ? { [COMPANY_HEADER]: companyId } : {}),
      ...(appSession ? { [APP_SESSION_HEADER]: appSession } : {}),
      ...(idempotencyKey ? { [IDEMPOTENCY_HEADER]: idempotencyKey } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  let parsedBody: unknown;
  try {
    parsedBody = text ? JSON.parse(text) : undefined;
  } catch {
    parsedBody = undefined;
  }

  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(parsedBody);
    if (parsed.success) {
      const e = parsed.data.error;
      const err = new ApiRequestError(res.status, e.code, e.message, e.requestId, e.details);
      if (isAppSessionError(err)) config.onAppSessionRejected(err);
      throw err;
    }
    throw new ApiRequestError(res.status, "HTTP_ERROR", `Erro ${res.status} ao chamar a API`);
  }
  return parsedBody as T;
}

/** Arquivo da API (com a sessão e a empresa ativa), sem salvar: o conteúdo e o nome sugerido. */
export async function apiFetchBlob(
  path: string,
  fallbackName: string,
): Promise<{ blob: Blob; name: string }> {
  const accessToken = await currentAccessToken();
  const companyId = getActiveCompanyId();
  const appSession = companyId ? await config.getAppSession(companyId) : null;
  const res = await fetch(`${config.baseUrl}${API_PREFIX}${path}`, {
    credentials: "omit",
    headers: {
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...(companyId ? { [COMPANY_HEADER]: companyId } : {}),
      ...(appSession ? { [APP_SESSION_HEADER]: appSession } : {}),
    },
  });
  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(await res.json().catch(() => undefined));
    if (parsed.success) {
      const e = parsed.data.error;
      const err = new ApiRequestError(res.status, e.code, e.message, e.requestId);
      if (isAppSessionError(err)) config.onAppSessionRejected(err);
      throw err;
    }
    throw new ApiRequestError(res.status, "HTTP_ERROR", `Erro ${res.status} ao baixar o arquivo`);
  }
  const disposition = res.headers.get("content-disposition") ?? "";
  const utf8 = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1];
  const name = utf8 ? decodeURIComponent(utf8) : fallbackName;
  return { blob: await res.blob(), name };
}

/** Salva um conteúdo já baixado como arquivo (download do navegador). */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Baixa um arquivo privado da API (com o login e a empresa ativa) e entrega ao
 * navegador como download. O arquivo nunca fica num endereço público.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const { blob, name } = await apiFetchBlob(path, fallbackName);
  saveBlob(blob, name);
}

/** Lê um arquivo escolhido pelo usuário como base64 (sem o prefixo data:). */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Falha ao ler o arquivo"));
    reader.readAsDataURL(file);
  });
}

/** Lista de textos em `details` (quando a API explica os problemas item a item). */
export function errorDetails(err: unknown): string[] {
  if (!(err instanceof ApiRequestError) || !Array.isArray(err.details)) return [];
  return err.details.filter((d): d is string => typeof d === "string");
}

/** Nova chave de idempotência (uma por clique/intenção; reutilize ao repetir o MESMO envio). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

const TECHNICAL_FALLBACK =
  "Há um dado inválido neste formulário. Revise os campos e tente de novo.";

/**
 * Mensagem para a pessoa: a da API (já em português, com o nome do campo). Se ainda assim
 * vier algo técnico (inglês, regex), mostra uma mensagem funcional; falha de rede vira
 * orientação de conexão. O detalhe técnico fica no log da API.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) {
    return looksTechnical(err.message) ? TECHNICAL_FALLBACK : err.message;
  }
  if (err instanceof TypeError) {
    return "Não foi possível falar com o servidor. Verifique a conexão e tente de novo.";
  }
  if (err instanceof Error) {
    return looksTechnical(err.message) ? "Erro inesperado. Tente de novo." : err.message;
  }
  return "Erro inesperado.";
}
